import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { AlertTriangle, ArrowDown } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const ProblemSection = () => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) {
      if (contentRef.current) {
        Array.from(contentRef.current.children).forEach(child => {
          child.style.opacity = '1';
          child.style.transform = 'none';
        });
      }
      return;
    }

    gsap.fromTo(
      contentRef.current?.children || [],
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: contentRef.current,
          start: 'top 80%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  }, [isMobile]);

  const painPoints = [
    'Fragmented systems that don\'t talk to each other',
    'Disconnected departments making siloed decisions',
    'Critical athlete data scattered across spreadsheets and apps',
    'No single source of truth for your organization'
  ];

  return (
    <section
      ref={sectionRef}
      className="section relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #0A0A0A 0%, #0F0F0F 50%, #0A0A0A 100%)' }}
      data-testid="problem-section"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D3FF62]/5 rounded-full blur-[200px] pointer-events-none" />
      </div>

      <div className="container relative z-10">
        <div ref={contentRef} className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/5 mb-8">
            <AlertTriangle size={16} className="text-white/60" />
            <span className="text-white/60 text-sm font-medium">The Problem</span>
          </div>

          {/* Headline */}
          <h2 className="display-large text-white mb-6">
            SPORTS ORGANIZATIONS AT EVERY LEVEL<br />
            <span className="gradient-text">FACE THE SAME CHALLENGE</span>
          </h2>

          {/* Description */}
          <p className="body-large text-white/70 mb-12 max-w-2xl mx-auto">
            Whether you're a professional franchise, a college athletic department, a high school program, 
            or a youth organization — your athletes, coaches, and staff deserve better tools.
          </p>

          {/* Pain Points */}
          <div className="grid sm:grid-cols-2 gap-4 mb-12 max-w-3xl mx-auto text-left">
            {painPoints.map((point, index) => (
              <motion.div
                key={index}
                initial={isMobile ? {} : { opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 * index }}
                viewport={{ once: true }}
                className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10"
              >
                <div className="w-2 h-2 rounded-full bg-red-400/80 mt-2 flex-shrink-0" />
                <span className="text-white/70 text-sm leading-relaxed">{point}</span>
              </motion.div>
            ))}
          </div>

          {/* Resolution */}
          <p className="text-lg text-white/50 mb-8 max-w-2xl mx-auto">
            We're building the infrastructure to fix that — a connected ecosystem of platforms 
            designed to bring every part of your organization together.
          </p>

          {/* Arrow to Products */}
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <a href="#ventures" className="inline-flex items-center gap-2 text-[#D3FF62] font-semibold hover:gap-3 transition-all">
              See Our Platforms
              <ArrowDown size={18} />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
